import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Container, Alert, Table, Modal, OverlayTrigger, Tooltip, Card, Spinner, Row, Col } from 'react-bootstrap';
import { inventoryItemAPI } from '../api/inventoryItemAPI';
import { locationAPI } from '../api/locationAPI';
import { warehouseAPI } from '../api/warehouseAPI';
import { vendorAPI } from '../api/vendorAPI';
import CostHistoryButton from '../components/CostHistoryButton';
import "bootstrap-icons/font/bootstrap-icons.css";
import Select from 'react-select';
import { formatErrorResponse } from '../utils/errorHandlers';
import QuantityPicker from '../components/QuantityPicker';

function BulkReceiveScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [redirecting, setRedirecting] = useState(false);
    const [locations, setLocations] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Get selected items from navigation state
    const { selectedItems, returnPath } = location.state || {};
    
    // Form state
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
    const [comments, setComments] = useState('');
    
    // Individual item data
    const [itemData, setItemData] = useState([]);

    // Initialize item data when component loads
    useEffect(() => {
        if (selectedItems && selectedItems.length > 0) {
            const initialItemData = selectedItems.map(item => ({
                stockItemId: item.id,
                description: item.description,
                manufacturer_name: item.manufacturer_name,
                gtin: item.gtin,
                sku: item.sku,
                image: item.image,
                case_pack: item.case_pack,
                uom: item.uom,
                quantity: '',
                lot: '',
                bestBeforeDate: '',
                unitCost: '',
                comments: ''
            }));
            setItemData(initialItemData);
        } else {
            // If no selected items, redirect back
            navigate('/stock-items');
        }
    }, [selectedItems, navigate]);

    // Fetch warehouses
    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const data = await warehouseAPI.getAll();
                setWarehouses(data);
            } catch (error) {
                setError(formatErrorResponse(error));
            }
        };
        fetchWarehouses();
    }, []);

    // Fetch vendors and set default to manufacturer
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const data = await vendorAPI.getAll();
                const vendorList = data.results || data || [];
                setVendors(vendorList);
                
                // Set default vendor to manufacturer if available
                if (selectedItems && selectedItems.length > 0 && vendorList.length > 0) {
                    const manufacturerId = selectedItems[0].manufacturer;
                    const manufacturerVendor = vendorList.find(vendor => vendor._id === manufacturerId);
                    if (manufacturerVendor) {
                        setSelectedVendor({ value: manufacturerVendor._id, label: manufacturerVendor.name });
                    }
                }
            } catch (error) {
                setError(formatErrorResponse(error));
            }
        };
        fetchVendors();
    }, [selectedItems]);

    // Fetch locations when warehouse is selected
    useEffect(() => {
        const fetchLocations = async () => {
            if (selectedWarehouse) {
                try {
                    const data = await locationAPI.getAll(selectedWarehouse.value, undefined, undefined, undefined, undefined, true);
                    // locationAPI.getAll returns paginated data, so we need to get the results
                    const locations = data.results || data || [];
                    setLocations(locations);
                } catch (error) {
                    setError(formatErrorResponse(error));
                }
            } else {
                setLocations([]);
                setSelectedLocation(null);
            }
        };
        fetchLocations();
    }, [selectedWarehouse]);

    // Update individual item data
    const updateItemData = (index, field, value) => {
        setItemData(prev => prev.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
        ));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsSubmitting(true);

        try {
            // Validate required fields
            if (!selectedLocation) {
                throw new Error('Please select a location');
            }

            if (!selectedVendor) {
                throw new Error('Please select a vendor');
            }

            // Validate that all items have quantity
            const invalidItems = itemData.filter(item => !item.quantity || item.quantity <= 0);
            if (invalidItems.length > 0) {
                throw new Error('All items must have a valid quantity greater than 0');
            }

            // Validate that all items have unit cost
            const invalidCostItems = itemData.filter(item => !item.unitCost || item.unitCost <= 0);
            if (invalidCostItems.length > 0) {
                throw new Error('All items must have a valid unit cost greater than 0');
            }

            // Prepare items data for batch receive (similar to BatchReceiveScreen)
            const itemsForBatch = itemData.map(item => ({
                stock_item_id: item.stockItemId,
                location: selectedLocation.value,
                quantity: parseFloat(item.quantity),
                lot: item.lot || '',
                bb_date: item.bestBeforeDate ? item.bestBeforeDate : null,
                cost: parseFloat(item.unitCost),
                notes: item.comments || comments || '', // Use item-specific comments or global comments
                warehouse_id: selectedWarehouse.value,
                vendor_id: selectedVendor.value
            }));

            // Use receiveBatch API like BatchReceiveScreen
            await inventoryItemAPI.receiveBatch(itemsForBatch);

            setSuccess(`Successfully received ${itemData.length} items`);
            setRedirecting(true);
            
            // Navigate to inventory list after short delay
            // Keep isSubmitting true until navigation to prevent double-clicks
            setTimeout(() => {
                navigate('/inventory');
            }, 2000);

        } catch (error) {
            setError(formatErrorResponse(error));
            setIsSubmitting(false); // Only reset on error
            setRedirecting(false); // Reset redirecting state on error
        }
    };

    const handleCancel = () => {
        if (returnPath) {
            navigate(returnPath);
        } else {
            navigate('/stock-items');
        }
    };

    if (!selectedItems || selectedItems.length === 0) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    No items selected for bulk receive. Please go back and select items.
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate('/stock-items')}>
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h4>Bulk Receive - {selectedItems.length} Items</h4>
                            <p className="mb-0 text-muted">
                                Manufacturer: <strong>{selectedItems[0].manufacturer_name}</strong>
                            </p>
                        </Card.Header>
                        <Card.Body>
                            {success && (
                                <Alert variant="success">
                                    {redirecting ? (
                                        <div className="d-flex align-items-center">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            {success} - Redirecting to inventory...
                                        </div>
                                    ) : (
                                        success
                                    )}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                {/* Shared Fields */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Vendor *</Form.Label>
                                    <Select
                                        value={selectedVendor}
                                        onChange={setSelectedVendor}
                                        options={vendors.map(v => ({ value: v._id, label: v.name }))}
                                        isSearchable
                                        placeholder="Select..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isDisabled={isSubmitting}
                                    />
                                    <Form.Text className="text-muted">
                                        Defaults to manufacturer ({selectedItems?.[0]?.manufacturer_name})
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Warehouse *</Form.Label>
                                    <Select
                                        value={selectedWarehouse}
                                        onChange={setSelectedWarehouse}
                                        options={warehouses.map(w => ({ value: w._id, label: w.name }))}
                                        isSearchable
                                        placeholder="Select..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isDisabled={isSubmitting}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Location *</Form.Label>
                                    <Select
                                        value={selectedLocation}
                                        onChange={setSelectedLocation}
                                        options={locations.map(l => ({ value: l._id, label: l.name, item_count: l.item_count }))}
                                        isSearchable
                                        placeholder="Select..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isDisabled={!selectedWarehouse || isSubmitting}
                                        formatOptionLabel={(option) => (
                                            <span>
                                                {option.label}
                                                {option.item_count != null && (
                                                    <span className={`location-badge${option.item_count === 0 ? ' is-empty' : ''}`}>
                                                        {option.item_count === 0 ? 'Empty' : `${option.item_count} items`}
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Label className="mb-0" style={{ minWidth: '120px' }}>Received Date *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={receivedDate}
                                            onChange={(e) => setReceivedDate(e.target.value)}
                                            max={new Date().toISOString().split('T')[0]}
                                            size="sm"
                                            required
                                            autoComplete="off"
                                            spellCheck="false"
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Global Comments (optional)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                        placeholder="Comments that will apply to all items..."
                                        autoComplete="off"
                                        spellCheck="true"
                                    />
                                </Form.Group>

                                {/* Individual Item Details */}
                                <h5>Item Details</h5>
                                <div className="table-responsive">
                                    <Table striped bordered hover style={{ minWidth: '1200px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{width: '60px'}}>Image</th>
                                                <th>Description</th>
                                                <th>GTIN</th>
                                                <th style={{minWidth: '280px'}}>Quantity *</th>
                                                <th style={{width: '120px'}}>LOT</th>
                                                <th style={{width: '150px'}}>Best Before</th>
                                                <th style={{minWidth: '180px'}}>Unit Cost *</th>
                                                <th style={{minWidth: '200px'}}>Item Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemData.map((item, index) => (
                                                <tr key={item.stockItemId}>
                                                    <td>
                                                        {item.image && (
                                                            <img 
                                                                src={item.image} 
                                                                alt="Item"
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    objectFit: 'contain'
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>{item.description}</td>
                                                    <td>{item.gtin}</td>
                                                    <td>
                                                        <QuantityPicker
                                                            casePack={item.case_pack || 1}
                                                            uom={item.uom || ''}
                                                            value={item.quantity}
                                                            onChange={(qty) => updateItemData(index, 'quantity', qty)}
                                                            required
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.lot}
                                                            onChange={(e) => updateItemData(index, 'lot', e.target.value)}
                                                            size="sm"
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="date"
                                                            value={item.bestBeforeDate || ''}
                                                            onChange={(e) => updateItemData(index, 'bestBeforeDate', e.target.value)}
                                                            size="sm"
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Form.Control
                                                                type="number"
                                                                min="0.01"
                                                                step="0.0001"
                                                                value={item.unitCost}
                                                                onChange={(e) => updateItemData(index, 'unitCost', e.target.value)}
                                                                size="sm"
                                                                required
                                                                style={{ flex: 1, minWidth: '90px' }}
                                                                autoComplete="off"
                                                                spellCheck="false"
                                                            />
                                                            <CostHistoryButton
                                                                stockItemId={item.stockItemId}
                                                                stockItemDescription={item.description}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.comments}
                                                            onChange={(e) => updateItemData(index, 'comments', e.target.value)}
                                                            size="sm"
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Error Message */}
                                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                                
                                {/* Action Buttons */}
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button 
                                        variant="secondary" 
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                {redirecting ? 'Redirecting...' : 'Receiving Items...'}
                                            </>
                                        ) : (
                                            `Receive ${itemData.length} Items`
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default BulkReceiveScreen;